/**
 * The rules of Products (CU-869fhuhy6, brief v3).
 *
 * A product is "one of our offers": a name and 1..N configuration rows,
 * one per site — which pricelist the engine READS the current price
 * from, how far the broker's markup may go (cap %), which pricelist it
 * WRITES, and the pricelists derived from that write (pricelist ± delta).
 * Each row may also say which of our offers the product is on that site
 * (tariff type, insurance); blank = any → the cheapest own row.
 *
 * Pure: no React, no fetch. The Setup tab, the wizard's product picker
 * and the Strategies table read the same answers. The server holds the
 * same rules (`services/rules.py`) and its 409 / 422 carry the same
 * sentences; the front is the first net, not the only one (UX v3 D15).
 * Ids are compared as strings: the catalogue's `id` is numeric, a saved
 * row keeps whatever the picker handed it.
 */
const same = (a, b) => a != null && b != null && String(a) === String(b)
const present = (v) => v !== null && v !== undefined && v !== ''

export const pricelistById = (pricelists, id) => (pricelists || []).find(p => same(p.id, id)) || null
const bySource = (pricelists, sourceId) => (pricelists || []).find(p => same(p.source_id, sourceId)) || null

export const PRODUCT_REASON = {
	none: 'The strategy needs a product. Pick one to continue.',
	gone: 'This product no longer exists in Setup › Products — choose another.',
	empty: 'This product has no configuration row yet — add one in Setup › Products.',
}

export const fmtDelta = (d) => (!d || d.v == null) ? '' : `${Number(d.v) > 0 ? '+' : ''}${d.v}${d.k === 'eur' ? ' €' : ' %'}`

/**
 * The write's delta of a row (CU-869fkr1a9, criterion 6) as `{ k, v }`, or
 * null when there is none: no value — empty, missing, or 0 — is "no delta",
 * whatever the % / € button shows (the control starts empty, UX Obs. 1).
 */
export function writeDeltaOf(row) {
	const d = row?.writeDelta
	if (!d || !present(d.v)) return null
	const v = Number(d.v)
	if (!Number.isFinite(v) || v === 0) return null
	return { k: d.k === 'eur' ? 'eur' : 'pct', v }
}

/** The distinct sites of a product's rows, as numbers, in row order. */
export function productSites(product) {
	const out = []
	for (const r of product?.rows || []) {
		if (r.siteId == null) continue
		const n = Number(r.siteId)
		if (!out.includes(n)) out.push(n)
	}
	return out
}

/** `"Prepaid · Zero excess"`, `"Prepaid"`, `"any offer"` — the kind of a row, by name, never by id. */
export function kindLabel(row, axes) {
	const parts = []
	if (present(row?.tariffType)) parts.push(axes.prepaidName(!!row.tariffType))
	if (present(row?.insuranceId)) parts.push(axes.insuranceName(row.insuranceId))
	return parts.length ? parts.join(' · ') : 'any offer'
}

/** Two rows have the same kind when both axes match — both blank counts as equal (brief v3: "no two blank, no two equal"). */
export function sameKind(a, b) {
	const t = (v) => (present(v) ? String(!!v) : '')
	const i = (v) => (present(v) ? String(v) : '')
	return t(a?.tariffType) === t(b?.tariffType) && i(a?.insuranceId) === i(b?.insuranceId)
}

/**
 * Two products sharing a site must have distinguishable kinds on that
 * site. `[{ siteId, a: { id, name }, b: { id, name }, kind: row }]`, one
 * entry per offending pair and site; `a.rowId` / `b.rowId` are the two
 * rows that clash, so the card can mark the guilty one (UX v3 D15). Rows
 * of the SAME product on the same site are not checked here — one row
 * per site is `productRowIssues`' job (brief Duda 2).
 */
export function productConflicts(list) {
	const out = []
	const all = list || []
	for (let i = 0; i < all.length; i++) for (let j = i + 1; j < all.length; j++) {
		const a = all[i], b = all[j]
		for (const ra of a.rows || []) {
			if (ra.siteId == null) continue
			for (const rb of b.rows || []) {
				if (rb.siteId == null || !same(ra.siteId, rb.siteId)) continue
				if (sameKind(ra, rb) && !out.some(c => same(c.siteId, ra.siteId) && c.a.id === a.id && c.b.id === b.id)) {
					out.push({ siteId: Number(ra.siteId), a: { id: a.id, name: a.name, rowId: ra.id }, b: { id: b.id, name: b.name, rowId: rb.id }, kind: ra })
				}
			}
		}
	}
	return out
}

/** The kind as the conflict sentence says it: `Prepaid · Todo Riesgo`, or `any` for a blank row (criterion 4: never "not set"). */
const offerWord = (row, axes) => (kindLabel(row, axes) === 'any offer' ? 'any' : kindLabel(row, axes))

/**
 * Criterion 5, said to the product being saved (`selfId`): *'Prepaid Todo
 * Riesgo' already covers DiscoverCars with the same offer (any) — set the
 * tariff type or the insurance on one of them.* Without `selfId`, the
 * first product of the pair is the one that already covers. The server's
 * 409 brings the same sentence (D15, `serverProblems`).
 */
export function conflictSentence(c, labelOf, axes, selfId = null) {
	const other = selfId != null && same(c.a.id, selfId) ? c.b : c.a
	return `'${other.name}' already covers ${labelOf(c.siteId)} with the same offer (${offerWord(c.kind, axes)}) — set the tariff type or the insurance on one of them.`
}

/** The other product of a conflict, seen from `selfId`: `{ id, name, rowId }`. */
export function conflictOther(c, selfId) { return same(c.a.id, selfId) ? c.b : c.a }
/** The row of `selfId` that clashes, or null when the product is not in the pair. */
export function conflictOwnRowId(c, selfId) {
	if (same(c.a.id, selfId)) return c.a.rowId
	if (same(c.b.id, selfId)) return c.b.rowId
	return null
}

const normName = (v) => String(v ?? '').trim().toLowerCase()

/**
 * Problems inside one product, as `[{ text, rowId }]` (`rowId` null for
 * the product-level ones): no name, a name another product has (criterion
 * 8; `others` = the saved products, the product itself excluded by id), no
 * rows, a row without site, read (D17: always saved with a read) or write,
 * a derived pricelist that is the write itself or derived twice in the row
 * (named by its code from `pricelists`, else its id), a write's delta
 * without a usable value, and a second row on a site the product already
 * has — one row per site and product (brief Duda 2, 422 `one_row_per_site`
 * on the server). Several rows of the product may write or derive the same
 * pricelist: the engine sends it once, the lowest value (CU-869fkr1a9,
 * criteria 4 and 11). `[]` when clean.
 */
function rowIssuesDetailed(product, others = [], pricelists = []) {
	const issues = []
	const rows = product?.rows || []
	const name = normName(product?.name)
	if (!name) issues.push({ text: 'Give the product a name.', rowId: null })
	else {
		const clash = (others || []).find(o => !same(o.id, product?.id) && normName(o.name) === name)
		if (clash) issues.push({ text: `Another product is already called "${clash.name}".`, rowId: null })
	}
	if (!rows.length) issues.push({ text: 'A product needs at least one row (site → read → cap → write).', rowId: null })
	const seen = new Set()
	rows.forEach((r, i) => {
		const n = i + 1
		const push = (text) => issues.push({ text, rowId: r.id })
		if (r.siteId == null) push(`Row ${n}: pick the site.`)
		if (r.readPricelistId == null) push(`Row ${n}: pick the pricelist it reads.`)
		if (r.writePricelistId == null) push(`Row ${n}: pick the pricelist it writes.`)
		// The net of criterion 6: the control cannot produce it, the server answers 422 `write_delta_incomplete`.
		const wd = r.writeDelta
		if (wd && present(wd.v) && (!Number.isFinite(Number(wd.v)) || (wd.k !== 'pct' && wd.k !== 'eur'))) push(`Row ${n}: the write's delta needs a value.`)
		if ((r.derived || []).some(d => d.pricelistId != null && same(d.pricelistId, r.writePricelistId))) push(`Row ${n}: a derived pricelist cannot be the write pricelist itself.`)
		// Same rule as the server's 422 `derived_pricelist_duplicate`, which names the pricelist too (criterion 3).
		const counted = new Map()
		for (const d of r.derived || []) if (d.pricelistId != null) counted.set(String(d.pricelistId), (counted.get(String(d.pricelistId)) || 0) + 1)
		for (const [id, times] of counted) {
			if (times > 1) push(`Row ${n}: ${pricelistById(pricelists, id)?.code || id} is already derived in this row — derive each pricelist once.`)
		}
		if (r.siteId != null) {
			const k = String(Number(r.siteId))
			if (seen.has(k)) push(`Row ${n}: a second row on the same site — one row per site; set its offer on the other row instead.`)
			seen.add(k)
		}
	})
	return issues
}

/** The sentences of `rowIssuesDetailed`; `productRowIssues.detailed(product, others, pricelists)` keeps the row ids. */
export function productRowIssues(product, others = [], pricelists = []) {
	return rowIssuesDetailed(product, others, pricelists).map(i => i.text)
}
productRowIssues.detailed = rowIssuesDetailed

/** The base of a PMS-linked pricelist, by code (`'another pricelist'` when the catalogue does not have it). */
const baseCode = (pl, pricelists) => bySource(pricelists, pl.base_pricelist_id)?.code || 'another pricelist'

/**
 * Why a pricelist cannot be the WRITE target of a row, or null when it
 * can: only a pricelist linked in the PMS (`base_pricelist_id`) — it
 * inherits its prices, so the way to price it is to write its base with a
 * delta (criterion 7). A pricelist derived in another row may be written
 * (CU-869fkr1a9, criterion 1 and UX Obs. 3). The short form is the
 * picker's; `linkedWriteSentence` is the whole sentence the server answers.
 */
export function writeExclusion(pl, pricelists) {
	if (!pl || !present(pl.base_pricelist_id)) return null
	const base = baseCode(pl, pricelists)
	return `linked in the PMS — inherits its prices from ${base}. Write ${base} with a delta and this one will inherit it`
}

/** Criterion 7 / UX Obs. 12, the server's own words (422 `linked_pricelist_write`): for the "Not saved" list. Null when not linked. */
export function linkedWriteSentence(pl, pricelists) {
	if (!pl || !present(pl.base_pricelist_id)) return null
	const base = baseCode(pl, pricelists)
	return `${pl.code} is linked in the PMS: it inherits its prices from ${base}. Write ${base} with a delta and ${pl.code} will inherit it.`
}

/**
 * `{ state: none | ok | invalid, reason, product, rows }` for a strategy's
 * product, judged from the products on screen. Invalid when the product is
 * gone, has no rows, or a row writes a pricelist the rules refuse (a
 * PMS-linked one). The strategies list carries the server's
 * own verdict (`row.product.state`); this one is for the wizard, which
 * holds the products and the catalogue and must answer before a save.
 */
export function productState(productId, list, pricelists) {
	if (productId == null || productId === '') return { state: 'none', reason: PRODUCT_REASON.none, product: null, rows: [] }
	const product = (list || []).find(p => same(p.id, productId)) || null
	if (!product) return { state: 'invalid', reason: PRODUCT_REASON.gone, product: null, rows: [] }
	const rows = product.rows || []
	if (!rows.length) return { state: 'invalid', reason: PRODUCT_REASON.empty, product, rows }
	for (const r of rows) {
		const pl = pricelistById(pricelists, r.writePricelistId)
		if (!pl) continue   // catalogue not loaded, or a code the tenant lost: the picker says so
		const why = writeExclusion(pl, pricelists)
		if (why) return { state: 'invalid', reason: `${product.name} writes ${pl.code}, which is ${why}.`, product, rows }
	}
	return { state: 'ok', reason: null, product, rows }
}

/**
 * The strategies on a product, by priority: `[{ id, name, priority,
 * enabled }]`. A strategy names its product as `productId` (the
 * Strategies API, `fromServer`); `exceptId` leaves one out — the one
 * being edited.
 */
export function usedBy(productId, strategies, exceptId = null) {
	if (productId == null) return []
	return (Array.isArray(strategies) ? strategies : [])
		.filter(s => same(s?.productId, productId) && !same(s?.id, exceptId))
		.sort((a, b) => (a.priority ?? 99) - (b.priority ?? 99))
		.map(s => ({ id: s.id, name: s.name, priority: s.priority, enabled: !!s.enabled }))
}

/** `"same kind as my product"` · `"every kind"` · `"Prepaid · Zero excess"` — one line for the table and the confirm diff. */
export function compareLabel(compare, axes) {
	const c = compare || {}
	if (c.mode === 'every') return 'every kind'
	if (c.mode === 'specific') return kindLabel({ tariffType: c.tariffType, insuranceId: c.insuranceId }, axes)
	return 'same kind as my product'
}

// ─── UX v3 (2026-10-20): the row's sentence, who else writes, who prices, the header's short kind ──

/** `+ CODE +10 % · CODE2 -5 €` for the derived of a row that have a pricelist. */
const derivedPhrase = (row, pricelists) => (row?.derived || [])
	.filter(d => d.pricelistId != null)
	.map(d => `${pricelistById(pricelists, d.pricelistId)?.code || '?'} ${fmtDelta(d.delta)}`.trim())
	.join(' · ')

/**
 * The sentence Curro wants to be able to say about a row (UX v3 D13):
 * *On DiscoverCars, our Prepaid · Zero excess reads 30 SC PREPAID, is
 * capped at 45 % and writes TEST SC RMS DS (+ TEST SC RMS DS +10 +10 %).*
 * Read = write → *reads and writes X*; no cap → the clause is dropped; a
 * blank kind → *any offer of ours (the cheapest own row)*; a write's
 * delta follows its code (*writes CANARIAS WEB +15 %*). Generated from
 * the row so the card and the wizard's picker say the same words.
 */
export function rowSentence(row, pricelists, axes, labelOf) {
	if (row?.siteId == null) return 'Pick the site.'
	const kind = kindLabel(row, axes)
	const who = kind === 'any offer' ? 'any offer of ours (the cheapest own row)' : `our ${kind}`
	const read = pricelistById(pricelists, row.readPricelistId)
	const write = pricelistById(pricelists, row.writePricelistId)
	const head = `On ${labelOf(row.siteId)}, ${who}`
	if (!read && !write) return `${head} — pick the pricelists it reads and writes.`
	if (!write) return `${head} reads ${read.code} — pick the pricelist it writes.`
	if (!read) return `${head} writes ${write.code} — pick the pricelist it reads.`
	const cap = row.capPct != null ? `, is capped at ${row.capPct} %` : ''
	const derived = derivedPhrase(row, pricelists)
	const tail = derived ? ` (+ ${derived})` : ''
	// The write's delta (CU-869fkr1a9, UX Obs. 1): "writes CANARIAS WEB +15 %".
	const wd = writeDeltaOf(row)
	const written = wd ? `${write.code} ${fmtDelta(wd)}` : write.code
	if (same(read.id, write.id)) return `${head} reads and writes ${written}${tail}${row.capPct != null ? `, capped at ${row.capPct} %` : ''}.`
	return `${head} reads ${read.code}${cap} and writes ${written}${tail}.`
}

// ─── One pricelist, one product (CU-869fm5juc, criteria 11-12) ──────────

/** The refusal's first sentence — the server's `ONE_PRICELIST_ONE_PRODUCT`, word for word. */
export const ONE_PRICELIST_ONE_PRODUCT = 'One pricelist, one product: a pricelist is written or derived by a single product.'

/**
 * Who among the OTHER products of `list` writes or derives `pricelistId`:
 * `[{ product, how: 'written' | 'derived', siteIds }]` — `written` when
 * any of its rows writes it, `derived` when they only derive it; the sites
 * of the rows that use it. Reading is never a use (criterion 12).
 */
export function pricelistUsersOf(pricelistId, product, list) {
	if (pricelistId == null) return []
	const out = []
	for (const p of list || []) {
		if (same(p.id, product?.id)) continue
		let writes = false, used = false
		const siteIds = []
		for (const r of p.rows || []) {
			const w = same(r.writePricelistId, pricelistId)
			const d = (r.derived || []).some(x => same(x.pricelistId, pricelistId))
			if (!w && !d) continue
			used = true
			writes = writes || w
			if (r.siteId != null && !siteIds.includes(Number(r.siteId))) siteIds.push(Number(r.siteId))
		}
		if (used) out.push({ product: p, how: writes ? 'written' : 'derived', siteIds })
	}
	return out
}

/** For one row: `{ write: users of its write, derived: { [pricelistId]: users } }` — what the row's inline errors read. */
export function pricelistUsers(row, product, list) {
	const derived = {}
	for (const d of row?.derived || []) if (d.pricelistId != null) derived[d.pricelistId] = pricelistUsersOf(d.pricelistId, product, list)
	return { write: pricelistUsersOf(row?.writePricelistId, product, list), derived }
}

/**
 * The sentence of one clash (plan D8, mock `#refused`): `clash` =
 * `{ code, use: 'write' | 'derived', otherName, otherHow: 'written' |
 * 'derived', siteIds }`; the sites are named by the front.
 */
export function clashSentence(clash, labelOf) {
	const other = clash.otherName
	const sites = (clash.siteIds || []).map(id => labelOf(id)).join(', ')
	const head = `${clash.code} is already ${clash.otherHow} by ${other} (${sites}).`
	if (clash.use === 'write') {
		return `${head} If this price follows ${other}, do not write it here: link it in the PMS to ${other}'s pricelist with a % or a fixed value.`
	}
	return `${head} Take it out of this row's derived pricelists, or link it in the PMS with a % or a fixed value.`
}

/** The red line under the guilty picker: "Written by X — one pricelist, one product" / "<code> is derived by X — …". */
export function clashHint(use, code, user) {
	const by = `${user.how === 'written' ? 'written' : 'derived'} by ${user.product?.name}`
	return use === 'write'
		? `${by.charAt(0).toUpperCase()}${by.slice(1)} — one pricelist, one product`
		: `${code} is ${by} — one pricelist, one product`
}

/**
 * The front's first net for criterion 11: every write or derived of
 * `draft` that another product (`others`, the saved ones) writes or
 * derives, as the "Not saved" list — the header once (`rowId: null`),
 * then one sentence per (row, pricelist, other product) with its row.
 */
export function pricelistClashes(draft, others, pricelists, labelOf) {
	const out = []
	const codeOf = (id) => pricelistById(pricelists, id)?.code || String(id)
	for (const r of draft?.rows || []) {
		const uses = [['write', r.writePricelistId], ...(r.derived || []).map(d => ['derived', d.pricelistId])]
		for (const [use, pid] of uses) {
			for (const u of pricelistUsersOf(pid, draft, others)) {
				out.push({ text: clashSentence({ code: codeOf(pid), use, otherName: u.product.name, otherHow: u.how, siteIds: u.siteIds }, labelOf), rowId: r.id })
			}
		}
	}
	return out.length ? [{ text: ONE_PRICELIST_ONE_PRODUCT, rowId: null }, ...out] : []
}

const siteIdsOf = (list) => (Array.isArray(list) ? list.map(o => Number(o?.value ?? o)).filter(Number.isFinite) : [])

/**
 * The strategies that use `productId` AND cover `siteId` — an empty site
 * scope covers every site of the product; an exclusion scope covers the
 * sites not listed. By priority, as `usedBy` returns them (D16-b). The
 * cell's "priced by" line; removing a row is `rowRemoval`'s.
 */
export function pricedBy(productId, siteId, strategies) {
	if (siteId == null) return []
	const list = Array.isArray(strategies) ? strategies : []
	return usedBy(productId, list, null).filter(u => {
		const s = list.find(x => same(x.id, u.id))
		const ids = siteIdsOf(s?.filterGroupSites)
		if (!ids.length) return true
		return ids.includes(Number(siteId)) !== !!s?.filterGroupSitesExclusion
	})
}

const scopeOf = (s) => ({ ids: siteIdsOf(s?.filterGroupSites), exclusion: !!s?.filterGroupSitesExclusion })

/**
 * What removing the row on `siteId` does to the strategies on the product
 * (CU-869fkr1a9, criteria 8-9; the same rule as webback's `site_removal`,
 * TL C2 of the plan review). Judged on the whole set of sites that leave in
 * this save — the sites of the SAVED rows (`saved`) that no longer have a
 * row in the draft (`product`), plus `siteId` when its row is the last one
 * on that site — never row by row: the draft keeps the removals until Save.
 * A strategy's effective sites are the saved sites ∩ its scope (inclusion:
 * the listed ones with a row; exclusion: the product's minus the listed;
 * empty: all). Then, by priority (`usedBy`):
 * - `only`: a scope that loses every effective site it has — refused; an
 *   inclusion is never emptied (empty would mean every site);
 * - `among`: a scope that loses some and keeps others, with `loses` and
 *   `keeps` (site ids, in row order) — the confirmation names them;
 * - `allSites`: an empty scope — it keeps the rest.
 * A strategy that loses nothing is in no list. A product never saved has
 * no strategies.
 */
export function rowRemoval(product, saved, siteId, strategies) {
	const none = { only: [], among: [], allSites: [] }
	if (siteId == null || product?._new || !saved) return none
	const savedSites = productSites(saved)
	const rows = product?.rows || []
	const leftOnSite = rows.filter(r => r.siteId != null && Number(r.siteId) === Number(siteId)).length
	const remaining = new Set(productSites(product))
	if (leftOnSite <= 1) remaining.delete(Number(siteId))
	const removed = savedSites.filter(s => !remaining.has(s))
	if (!removed.length) return none
	const list = Array.isArray(strategies) ? strategies : []
	const out = { only: [], among: [], allSites: [] }
	for (const u of usedBy(saved.id ?? product?.id, list, null)) {
		const { ids, exclusion } = scopeOf(list.find(x => same(x.id, u.id)))
		if (!ids.length) { out.allSites.push(u); continue }
		const effective = savedSites.filter(s => ids.includes(s) !== exclusion)
		const loses = effective.filter(s => removed.includes(s))
		if (!loses.length) continue
		const keeps = effective.filter(s => !removed.includes(s))
		if (!keeps.length) out.only.push({ ...u, sites: effective })
		else out.among.push({ ...u, loses, keeps })
	}
	return out
}

/**
 * The pricelists a product's rows write or derive, keyed by pricelist id
 * (string): `[{ row: n, kind: 'write' | 'derived', delta }]` in row order,
 * `delta` as `{ k, v }` or null. A key with entries of more than one row is
 * a pricelist the engine sends once, with the lowest value (CU-869fkr1a9,
 * criteria 10-11).
 */
export function sharedInProduct(product) {
	const out = new Map()
	const add = (id, entry) => {
		const k = String(id)
		if (!out.has(k)) out.set(k, [])
		out.get(k).push(entry)
	}
	;(product?.rows || []).forEach((r, i) => {
		if (r.writePricelistId != null) add(r.writePricelistId, { row: i + 1, kind: 'write', delta: writeDeltaOf(r) })
		for (const d of r.derived || []) if (d.pricelistId != null) add(d.pricelistId, { row: i + 1, kind: 'derived', delta: d.delta ?? null })
	})
	return out
}

const rowsOf = (entries) => [...new Set((entries || []).map(e => e.row))].sort((a, b) => a - b)

/**
 * The info line under a pricelist written or derived by more than one row
 * of the product (UX Obs. 4 and re-check), or null when only one row has
 * it: plain writes say *net*, anything with a derived or a delta says
 * *value* — the value compared is no longer the net. `differ` (a pair from
 * `sharedTiersDiffer`) adds the amber, non-blocking `warn` (TL Obs. 3).
 */
export function sharedNote(entries, code, differ = null) {
	const rows = rowsOf(entries)
	if (rows.length < 2) return null
	const n = rows.length, list = rows.join(', ')
	const writesOnly = entries.every(e => e.kind === 'write')
	const derivedOnly = entries.every(e => e.kind === 'derived')
	const text = writesOnly && entries.every(e => !e.delta)
		? `Written by ${n} rows (${list}): sent once, with the lowest net.`
		: derivedOnly
			? `${code} is derived by ${n} rows (${list}): sent once, with the lowest value.`
			: `${code} is written or derived by ${n} rows (${list}): sent once, with the lowest value.`
	return { text, warn: differ ? `brackets differ between rows ${differ[0]} and ${differ[1]}` : null }
}

/**
 * The first pair of rows `[n, m]` that write or derive `pricelistId` with
 * different brackets — each row's own bracket config, else its read
 * pricelist's — or null. Never blocks the save: with other brackets the
 * engine refuses the ladder (`ladder_shape_mismatch` in Activity), it never
 * writes a wrong price, and the brackets resync from the PMS anyway (TL
 * Obs. 3).
 */
export function sharedTiersDiffer(product, pricelistId, pricelists) {
	const rows = product?.rows || []
	const contributing = rows
		.map((r, i) => ({ r, n: i + 1 }))
		.filter(({ r }) => same(r.writePricelistId, pricelistId) || (r.derived || []).some(d => same(d.pricelistId, pricelistId)))
	const tiersOf = (r) => (Array.isArray(r.tierConfig) && r.tierConfig.length ? r.tierConfig : (pricelistById(pricelists, r.readPricelistId)?.tiers || []))
	for (let i = 0; i < contributing.length; i++) for (let j = i + 1; j < contributing.length; j++) {
		if (tiersDiffer({ tiers: tiersOf(contributing[i].r) }, { tiers: tiersOf(contributing[j].r) })) return [contributing[i].n, contributing[j].n]
	}
	return null
}

/** `"Sunny Cars · Prepaid, Todo Riesgo — DiscoverCars · any"`: the kind per site, short, for a folded card header (D13). */
export function kindSummary(product, axes, labelOf) {
	const bySite = new Map()
	for (const r of product?.rows || []) {
		if (r.siteId == null) continue
		const k = kindLabel(r, axes)
		const short = k === 'any offer' ? 'any' : k.replace(/ · /g, ', ')
		const key = Number(r.siteId)
		bySite.set(key, bySite.has(key) ? `${bySite.get(key)} / ${short}` : short)
	}
	return [...bySite.entries()].map(([id, k]) => `${labelOf(id)} · ${k}`).join(' — ')
}

/** True when both pricelists have brackets synced and they differ (the soft warning under Reads, criterion 3). */
export function tiersDiffer(read, write) {
	const a = read?.tiers || [], b = write?.tiers || []
	if (!a.length || !b.length) return false
	const key = (t) => `${t.duration_from}-${t.duration_to ?? ''}`
	const sa = new Set(a.map(key)), sb = new Set(b.map(key))
	if (sa.size !== sb.size) return true
	for (const k of sa) if (!sb.has(k)) return true
	return false
}

// ─── The server's refusal, in the card's words (UX v3 D15.3) ─────────────

/** The codes the card answers with the dialog that has no red button (criteria 7 and 10), not with the "Not saved" list. */
export const REFUSED_IN_USE_CODES = ['product_in_use', 'row_in_use']

/**
 * A 409 / 422 of the Products API as `[{ text, rowId }]` for the "Not
 * saved" list. The server sends `site_id` and the kind; the site's name
 * is the front's (its catalogue), so `product_kind_conflict` is rebuilt
 * here with `conflictSentence`'s words. Every other code travels as the
 * server's own sentence — criterion 2's for a linked write — with the
 * row it names when `details.row_id` says which. Not an `ApiError`, or
 * an in-use refusal: `[]`, the caller shows it another way.
 */
export function serverProblems(error, labelOf, axes) {
	if (!error || error.name !== 'ApiError') return []
	if (REFUSED_IN_USE_CODES.includes(error.code)) return []
	const d = error.details || {}
	const rowId = d.row_id ?? null
	if (error.code === 'product_kind_conflict') {
		const kind = { tariffType: d.kind?.is_prepaid ?? null, insuranceId: d.kind?.ps_insurance_id ?? null }
		const c = { siteId: d.site_id, a: { id: d.other_product_id, name: d.other_product_name || 'Another product' }, b: { id: null }, kind }
		return [{ text: conflictSentence(c, labelOf, axes), rowId }]
	}
	if (error.code === 'pricelist_of_another_product' && Array.isArray(d.clashes) && d.clashes.length) {
		// The server names sites by id; the card names them (plan D8).
		return [{ text: ONE_PRICELIST_ONE_PRODUCT, rowId: null }, ...d.clashes.map(c => ({
			text: clashSentence({ code: c.code, use: c.use, otherName: c.other_product_name, otherHow: c.other_how, siteIds: c.site_ids }, labelOf),
			rowId: c.row_id ?? null,
		}))]
	}
	if (error.code === 'duplicate_product_name') {
		return [{ text: d.name ? `Another product is already called "${d.name}".` : error.message, rowId: null }]
	}
	if (error.code === 'validation_error' && Array.isArray(d.errors) && d.errors.length) {
		return d.errors.map(e => ({ text: `${e.loc} ${e.msg}`, rowId }))
	}
	return [{ text: error.message, rowId }]
}
